/**
 * Selects how the call screen's output AudioContext reaches the device
 * speaker, so mobile OSes classify the conversation as a voice call instead
 * of media playback.
 *
 * Background: Web Audio played through `AudioContext.destination` rides the
 * media audio stream on phones (Android STREAM_MUSIC, and on iOS it follows
 * whatever audio session the page has). Real calling apps (Google Meet mobile
 * web) get voice-call routing: loudspeaker output, hardware volume rocker on
 * call volume, audible even when media volume is muted, and echo cancellation
 * that knows about the played audio. Each platform needs a different path to
 * that classification, captured here as an {@link AudioOutputRoute}.
 */

/**
 * How AI playback should leave the output AudioContext.
 *
 * - `direct`: connect straight to `AudioContext.destination`. Desktop default;
 *   nothing changes off mobile.
 * - `media-element`: route through `createMediaStreamDestination()` into an
 *   HTMLAudioElement. Used on iOS (see {@link selectAudioOutputRoute}) and on
 *   desktop when the user picked a specific speaker (setSinkId needs a media
 *   element).
 * - `webrtc-loopback`: additionally pass the stream through a local
 *   RTCPeerConnection pair so the audio plays as a *remote WebRTC track*.
 *   Required on Android Chrome, which only grants voice-call routing
 *   (STREAM_VOICE_CALL + MODE_IN_COMMUNICATION) and AEC reference coverage to
 *   the WebRTC playout path.
 */
export type AudioOutputRoute = 'direct' | 'media-element' | 'webrtc-loopback';

/** Platform buckets that matter for audio routing decisions. */
export type MobileAudioPlatform = 'ios' | 'android' | 'other';

/**
 * Everything {@link selectAudioOutputRoute} needs to know, gathered in one
 * plain object so the decision logic is pure and unit-testable.
 */
export interface AudioRouteEnvironment {
  /** `navigator.userAgent`, or empty string when unavailable. */
  userAgent: string;
  /** `navigator.maxTouchPoints`; distinguishes iPads masquerading as Macs. */
  maxTouchPoints: number;
  /** Whether `HTMLAudioElement.prototype` exposes `setSinkId`. */
  hasSetSinkId: boolean;
  /** Whether `RTCPeerConnection` exists (needed for the Android loopback). */
  hasRTCPeerConnection: boolean;
  /** The user's chosen output device from the lobby, if any (desktop only). */
  preferredSpeakerId?: string;
}

/**
 * Buckets the runtime platform for audio-routing purposes.
 *
 * iPadOS Safari reports a macOS user agent by default; real Macs report
 * `maxTouchPoints` 0 while iPads report 5, so a Macintosh UA with touch
 * points is treated as iOS. Every iOS browser (including Chrome, which
 * reports CriOS) is WebKit and behaves like Safari for audio sessions.
 */
export function detectMobileAudioPlatform(
  userAgent: string,
  maxTouchPoints: number,
): MobileAudioPlatform {
  if (/Android/i.test(userAgent)) return 'android';
  if (/iPad|iPhone|iPod/.test(userAgent)) return 'ios';
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return 'ios';
  return 'other';
}

/**
 * Picks the output route for the current environment.
 *
 * Per-platform reasoning (each branch exists for a specific OS quirk):
 *
 * - **Android**: only audio rendered by the WebRTC playout path is placed on
 *   the voice-call stream (STREAM_VOICE_CALL) and included in the echo
 *   canceller's reference signal. Web Audio and plain media elements stay on
 *   STREAM_MUSIC no matter what, so a local RTCPeerConnection loopback is
 *   required. If RTCPeerConnection is somehow unavailable, fall back to a
 *   media element (degraded: media-stream routing, but playback still works).
 * - **iOS**: once getUserMedia holds a live mic, WebKit switches the whole
 *   page to the play-and-record audio session, so call-volume classification
 *   comes for free. The media element (with playsinline) is still used
 *   because WebKit's speaker-vs-earpiece default is chosen per rendering
 *   path, and media-element playback is the route calling apps rely on for
 *   loudspeaker output (WebKit bug 218012 tracks the earpiece default).
 * - **Desktop**: `setSinkId` only exists on media elements, so a preferred
 *   speaker forces the media-element route (pre-existing behavior).
 *   Otherwise connect straight to the context destination.
 */
export function selectAudioOutputRoute(env: AudioRouteEnvironment): AudioOutputRoute {
  const platform = detectMobileAudioPlatform(env.userAgent, env.maxTouchPoints);
  if (platform === 'android') {
    return env.hasRTCPeerConnection ? 'webrtc-loopback' : 'media-element';
  }
  if (platform === 'ios') {
    return 'media-element';
  }
  if (env.preferredSpeakerId && env.hasSetSinkId) {
    return 'media-element';
  }
  return 'direct';
}

/**
 * Snapshots the live browser environment into an {@link AudioRouteEnvironment}.
 * Kept separate from the pure selection logic so tests can fabricate
 * environments without stubbing globals.
 */
export function getAudioRouteEnvironment(preferredSpeakerId?: string): AudioRouteEnvironment {
  return {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent ?? '' : '',
    maxTouchPoints: typeof navigator !== 'undefined' ? navigator.maxTouchPoints ?? 0 : 0,
    hasSetSinkId:
      typeof HTMLAudioElement !== 'undefined' && 'setSinkId' in HTMLAudioElement.prototype,
    hasRTCPeerConnection: typeof RTCPeerConnection !== 'undefined',
    preferredSpeakerId,
  };
}

/**
 * The mic devices to walk through to latch Android's call route onto the
 * loudspeaker: bounce to `bounceId` (when available), then land on
 * `speakerphoneId`.
 */
export interface AndroidCallRoutePlan {
  /** The synthetic "Speakerphone" input device to end on. */
  speakerphoneId: string;
  /** A different built-in input to bounce through first, or null if none. */
  bounceId: string | null;
}

/**
 * Plans the Android mic bounce that pins the call to the loudspeaker, or
 * returns null to leave routing alone.
 *
 * Android Chrome exposes no output-side device API (setSinkId is unavailable
 * there, crbug 41276355; enumerateDevices lists only a 'default' output).
 * Instead, the OS pairs the communication OUTPUT to the requested INPUT:
 * Chromium exposes synthetic built-in mics ("Speakerphone", "Headset
 * earpiece", ...). But the pairing only latches on a device request made
 * while communication mode is already active: Chromium enters
 * MODE_IN_COMMUNICATION when the FIRST capture opens and issues its device
 * selection just before that flip (ordering race in
 * media/audio/android/audio_manager_android.cc), so whatever the session
 * starts on never latches. The reliable page-level trigger is a GENUINE
 * device change while a capture stays open: bounce to another built-in mic,
 * then land on the speakerphone, each acquired before the previous capture
 * stops (a zero-capture moment resets communication mode).
 *
 * Returns null (leave routing alone) when:
 * - external audio hardware is present (Bluetooth/wired/USB): Chrome pairs
 *   the call to it, and stomping user hardware would be wrong;
 * - no distinct speakerphone-labeled input exists (labels are localized on
 *   some OEM builds; without a match there is nothing safe to request).
 *
 * The bounce partner prefers the earpiece-labeled input (the sequence proven
 * on-device) and falls back to any other built-in; `bounceId` is null when
 * the speakerphone is the only concrete input, in which case callers can
 * still try the direct request.
 */
export function selectAndroidCallRoutePlan(
  devices: Array<Pick<MediaDeviceInfo, 'deviceId' | 'kind' | 'label'>>,
): AndroidCallRoutePlan | null {
  const inputs = devices.filter((d) => d.kind === 'audioinput');

  // 'Headset earpiece' (the phone's receiver) must not count as external
  // hardware, so match explicit external tokens rather than 'headset'.
  const hasExternalHardware = inputs.some((d) => /bluetooth|wired|usb/i.test(d.label));
  if (hasExternalHardware) return null;

  const concrete = inputs.filter(
    (d) => d.deviceId !== 'default' && d.deviceId !== 'communications',
  );
  const speakerphone = concrete.find((d) => /speaker/i.test(d.label));
  if (!speakerphone) return null;

  const partners = concrete.filter((d) => d.deviceId !== speakerphone.deviceId);
  const bounce = partners.find((d) => /earpiece/i.test(d.label)) ?? partners[0] ?? null;
  return { speakerphoneId: speakerphone.deviceId, bounceId: bounce ? bounce.deviceId : null };
}
